import React from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, StepChip, GradientButton, CTABanner, InfoCard } from '../../components/common/Primitives';
import { SP_HOW } from '../../lib/mockStudent';
import { Check, WifiOff, Wifi, PlayCircle } from 'lucide-react';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';

export default function StudentHowItWorks() {
  const d = SP_HOW;
  return (
    <PageShell>
      {/* HERO with video placeholder */}
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <Eyebrow color="purple">{d.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 text-[40px] md:text-[54px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight text="No software licence fee through June 2027" /></div>
              <div className="mt-6 inline-flex items-center rounded-full bg-brand-gradient px-4 py-2 text-[12px] font-bold tracking-wide text-white shadow-soft">{d.hero.badge}</div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-cyan-50 p-6 md:p-8 shadow-soft">
              <div className="aspect-video rounded-2xl bg-brand-gradient grain relative flex items-center justify-center overflow-hidden">
                <span className="absolute top-3 left-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold tracking-widest text-navy">COMING SOON</span>
                <PlayCircle className="h-14 w-14 text-white/70" />
              </div>
              <div className="mt-4 text-[11.5px] font-bold tracking-widest text-purple">HOW IT WORKS · COMING SOON</div>
              <p className="mt-1 text-[13px] text-ink-500">The walkthrough video is being recorded — check back shortly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.journey.eyebrow} title={d.journey.title} desc={d.journey.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {d.journey.steps.map((s,i)=>(<div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft flex items-start gap-3"><StepChip n={s.n} color={['purple','royal','cyan','mint','purple','royal'][i]} /><div><div className="text-[14.5px] font-bold text-navy">{s.title}</div><div className="mt-1 text-[13px] text-ink-500">{s.desc}</div></div></div>))}
          </div>
        </div>
      </section>

      {/* FAMILY (moved above CONNECTIVITY) */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.family.eyebrow} title={d.family.title} desc={d.family.desc} />
          <div className="mt-8 rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50/50 via-white to-cyan-50/50 p-6 md:p-8 shadow-soft">
            <Eyebrow color="purple">{d.family.subEyebrow}</Eyebrow>
            <h4 className="mt-2 text-[22px] font-bold text-navy">{d.family.subTitle}</h4>
            <p className="mt-2 max-w-2xl text-[13.5px] text-ink-500">{d.family.subDesc}</p>
            <ul className="mt-5 grid grid-cols-1 gap-2 md:grid-cols-3">{d.family.subItems.map((s,i)=>(<li key={i} className="flex items-start gap-2 text-[13px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {s}</li>))}</ul>
          </div>
        </div>
      </section>

      {/* CONNECTIVITY (moved below FAMILY) */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.connectivity.eyebrow} title={d.connectivity.title} desc={d.connectivity.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-mint-50 bg-mint-50/40 p-6 shadow-soft">
              <div className="flex items-center gap-3"><span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-mint-50 text-mint"><WifiOff className="h-5 w-5" /></span><span className="rounded-full bg-mint-50 px-3 py-1 text-[11px] font-bold tracking-widest text-mint-600">{d.connectivity.offline.badge}</span></div>
              <div className="mt-4 text-[16px] font-bold text-navy">{d.connectivity.offline.title}</div>
              <ul className="mt-3 space-y-2">{d.connectivity.offline.items.map((s,i)=>(<li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {s}</li>))}</ul>
            </div>
            <div className="rounded-2xl border border-royal-50 bg-royal-50/40 p-6 shadow-soft">
              <div className="flex items-center gap-3"><span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-royal-50 text-royal"><Wifi className="h-5 w-5" /></span><span className="rounded-full bg-royal-50 px-3 py-1 text-[11px] font-bold tracking-widest text-royal-600">{d.connectivity.online.badge}</span></div>
              <div className="mt-4 text-[16px] font-bold text-navy">{d.connectivity.online.title}</div>
              <ul className="mt-3 space-y-2">{d.connectivity.online.items.map((s,i)=>(<li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-royal" /> {s}</li>))}</ul>
            </div>
          </div>
        </div>
      </section>

      {/* RESPONSIBILITIES (moved above ACCOUNTS) */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.responsibilities.eyebrow} title={d.responsibilities.title} desc={d.responsibilities.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.responsibilities.items.map((r,i)=>(<InfoCard key={i} letter={r.letter} color={r.color} title={r.title} desc={r.desc} />))}
          </div>
        </div>
      </section>

      {/* ACCOUNTS (redesigned) */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.account.eyebrow} title={d.account.title} desc={d.account.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.15fr_1fr]">
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 md:p-8 shadow-soft">
              <div className="text-[13px] font-bold text-navy">{d.account.withAcc.title}</div>
              <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {d.account.withAcc.items.map((s,i)=>(<li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {s}</li>))}
              </ul>
              <p className="mt-5 rounded-lg bg-canvas-100 p-3 text-[13px] text-ink-500">{d.account.note}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <GradientButton to="/access-request">Request Access</GradientButton>
                <GradientButton to="/student-parent/access" variant="outline">View Family Access</GradientButton>
              </div>
            </div>
            <div className="rounded-2xl bg-navy p-6 md:p-8 text-white shadow-card">
              <div className="text-[11.5px] font-bold tracking-widest text-cyan">WHAT AN ACCOUNT CONNECTS</div>
              <div className="mt-2 text-[18px] font-bold">Individual view, shared context</div>
              <div className="mt-5 space-y-3">
                {[
                  { k: 'VR', v: 'Module completion and time spent' },
                  { k: 'Q',  v: 'Quiz scores by conceptual / applied / theoretical' },
                  { k: 'R',  v: 'Chapter videos and visual resources visited' },
                  { k: 'P',  v: 'Simplified summary for parents' },
                ].map((r,i)=>(
                  <div key={i} className="flex items-center gap-3 rounded-xl bg-white/5 p-3 border border-white/10">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-cyan/20 text-cyan font-bold text-[12px]">{r.k}</span>
                    <span className="text-[13.5px]">{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title="See exactly how a session unfolds at home." desc="Request access to discuss the setup that fits your child and family." primary={{ label: 'Request Access', to: '/access-request' }} secondary={{ label: 'Family Access Setup', to: '/student-parent/access' }} />
        </div>
      </section>
    </PageShell>
  );
}
